import Cookies from "js-cookie";

// The AcesWeb endpoint reads the handoff token from this form field (AC-15496 token contract).
const TOKEN_FORM_FIELD = "token";
const SSO_ENDPOINT = "/api/v1/freshdesk-sso";

/**
 * Reasons a Knowledge Center launch can fail, tagged onto the thrown error as `reason` so the
 * caller can tell them apart. Only NOT_CONFIGURED means the feature is genuinely unavailable;
 * the others are transient and the launcher should stay visible.
 */
export const FRESHDESK_SSO_NOT_CONFIGURED = "not-configured";
export const FRESHDESK_SSO_POPUP_BLOCKED = "popup-blocked";
export const FRESHDESK_SSO_REQUEST_FAILED = "request-failed";

/**
 * Builds a tagged launch error. The tag is a plain property rather than a subclass so callers
 * can branch on `error.reason` without depending on `instanceof` surviving transpilation.
 */
function ssoError(message, reason, status = null) {
  const error = new Error(message);
  error.reason = reason;
  error.status = status;
  return error;
}

function buildHeaders() {
  return {
    "Content-Type": "application/json",
    Accept: "application/json",
    Authorization: `Bearer ${Cookies.get("token")}`,
  };
}

/**
 * Ask the backend whether the Freshdesk (Knowledge Center) SSO handoff is configured.
 * The secret/URL are provisioned by infra out-of-band, so "not configured" is an expected
 * state — the launcher stays hidden until this returns true. Returns false on any error.
 */
export async function fetchFreshdeskSsoConfigured() {
  try {
    const response = await fetch(SSO_ENDPOINT, {
      method: "GET",
      headers: buildHeaders(),
    });
    if (!response.ok) {
      return false;
    }
    const data = await response.json();
    return data?.configured === true;
  } catch {
    return false;
  }
}

/**
 * Reports whether `tab` is a blank tab this call just created, as opposed to an existing
 * Knowledge Center tab that `window.open` handed back because the window name matched.
 *
 * A tab we just created is same-origin and still at about:blank. An existing Knowledge Center
 * tab has navigated on to AcesWeb/Freshdesk, so reading its location throws a cross-origin
 * SecurityError — which is exactly the signal that it is not ours to close.
 */
function isTabWeJustCreated(tab) {
  try {
    return tab.location.href === "about:blank";
  } catch {
    return false;
  }
}

/**
 * Open the Freshdesk Knowledge Center in a new tab, SSO'd via AcesWeb.
 *
 * Opens the tab synchronously (inside the click gesture, so popup blockers allow it), then
 * fetches a short-lived handoff token and form-POSTs it into that tab. The POST is a real
 * top-level navigation — not a fetch — so AcesWeb's redirect chain to Freshdesk runs in the
 * new tab, and the token travels in the request body (never in a URL). referrerPolicy is
 * suppressed on the form itself as belt-and-braces.
 */
export async function openFreshdeskKnowledgeCenter() {
  const windowName = "bcaKnowledgeCenter";
  const newTab = window.open("", windowName);

  // Pop-up blocked: window.open returned null. Fail loudly and before minting anything —
  // form.submit() below does not throw when its target name resolves to nothing, so
  // continuing would burn a single-use token and then resolve successfully having done
  // nothing at all, leaving the caller no way to tell the user.
  if (!newTab) {
    throw ssoError(
      "Freshdesk SSO handoff failed: the Knowledge Center pop-up was blocked by the browser",
      FRESHDESK_SSO_POPUP_BLOCKED
    );
  }

  // Decide ownership up front, while the tab is still untouched: the fixed window name means
  // window.open can return an already-open Knowledge Center tab, and closing that on failure
  // would destroy whatever the user was doing in it.
  const weCreatedTheTab = isTabWeJustCreated(newTab);

  try {
    const response = await fetch(SSO_ENDPOINT, {
      method: "POST",
      headers: buildHeaders(),
    });
    if (!response.ok) {
      // 503 is the backend's documented fail-closed response when the SSO secret/URL are not
      // provisioned - the one case where the feature really is unavailable and hiding the
      // launcher is correct. Every other status is transient (401, 500, gateway errors) and
      // must not cost the user the menu item.
      throw ssoError(
        `Freshdesk SSO handoff failed (${response.status})`,
        response.status === 503
          ? FRESHDESK_SSO_NOT_CONFIGURED
          : FRESHDESK_SSO_REQUEST_FAILED,
        response.status
      );
    }
    const { acesUrl, token } = await response.json();

    const form = document.createElement("form");
    form.method = "POST";
    form.action = acesUrl;
    form.target = windowName;
    form.referrerPolicy = "no-referrer";
    form.style.display = "none";

    const tokenInput = document.createElement("input");
    tokenInput.type = "hidden";
    tokenInput.name = TOKEN_FORM_FIELD;
    tokenInput.value = token;
    form.appendChild(tokenInput);

    document.body.appendChild(form);
    form.submit();
    form.remove();
  } catch (error) {
    // Don't leave a blank tab behind when the handoff fails — but only close the tab if this
    // call opened it. A reused Knowledge Center tab holds live content the user did not ask
    // us to discard.
    if (weCreatedTheTab) {
      newTab.close();
    }

    // Guarantee every rejection carries a reason, so the caller never has to treat an
    // untagged error as "unavailable". A raw fetch/JSON failure is transient by definition.
    if (error && error.reason) {
      throw error;
    }
    throw ssoError(
      error?.message || "Freshdesk SSO handoff failed",
      FRESHDESK_SSO_REQUEST_FAILED
    );
  }
}
